import { Container, Text as CharaText, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { FollowListings } from '~/common/components/follow-listings';
import { FollowListingsModal } from '~/common/components/follow-listings-modal';
import { SmallSimplifiedBackButtonHeader } from '~/common/components/header/small-simplified-back-button-header';
import { Image } from '~/common/components/image';
import { Link } from '~/common/components/link';
import { Paper } from '~/common/components/paper';
import { UsernameWithBadges } from '~/common/components/user/username-with-badges';
import type { Props as MinimalLayoutProps } from '~/common/layouts/minimal-layout';
import { FollowListModalType } from '~/common/types/follow';
import rightArrowIconGray from '~/features/my_page/assets/right_arrow_gray.svg';
import { UserHeader } from '~/features/my_page/components/user-header';
import { usePage } from '~/features/my_page/hooks';
import { routes } from '~/utils/routes';
import { Color, Media, FontSize } from '~/utils/styles/enums';

const MinimalLayout = dynamic<MinimalLayoutProps>(() =>
  import('~/common/layouts/minimal-layout').then(
    ({ MinimalLayout }) => MinimalLayout
  )
);

export const Page: NextPage = () => {
  const {
    pageMetaData,
    breadcrumbList,
    checkingSession,
    tellerUser,
    vipBadge,
    officialBadge,
    screenLarge,
    toNovelStudio,
    SNSProviders,
    hasEmailProvider,
    hasPhoneProvider,
    hasPaymentInformation,
    followingCount,
    isMobile,
    followListModalType,
    handleTabClick,
    handleUserFollowStatsClick,
    isFollowListingsModalOpen,
    closeFollowListingsModal,
  } = usePage();

  if (isFollowListingsModalOpen && tellerUser && (isMobile || !screenLarge)) {
    return (
      <>
        <SmallSimplifiedBackButtonHeader
          noBorder
          title={
            tellerUser ? (
              <UsernameWithBadges
                header
                username={tellerUser.name}
                isOfficialWriter={tellerUser.isOfficialWriter}
                isVip={tellerUser.isVipUser}
              />
            ) : null
          }
          backCallback={closeFollowListingsModal}
        />

        <PageWrapper>
          <FollowListings
            userId={tellerUser.id}
            type={followListModalType || FollowListModalType.FOLLOWERS}
            handleTabClick={handleTabClick}
          />
        </PageWrapper>
      </>
    );
  }

  return (
    <>
      <MinimalLayout
        metaData={pageMetaData}
        breadcrumbList={breadcrumbList}
        pageInnerProps={{
          fullWidth: true,
        }}
        wrapperStyles={{
          background: Color.STRONG_BG,
        }}
      >
        {!checkingSession && tellerUser && (
          <>
            <Container
              padding={{ base: '16px', md: '40px' }}
              background={Color.PRIMARY}
              borderRadius={{
                base: 0,
                md: '8px',
              }}
              maxWidth="3xl"
            >
              <UserHeader
                tellerUser={tellerUser}
                vipBadge={vipBadge}
                officialBadge={officialBadge}
                screenLarge={screenLarge}
                followingCount={followingCount}
                onFollowStatsClick={handleUserFollowStatsClick}
              />
              <VStack spacing="8" alignItems="flex-start">
                <OptionWrapper>
                  <Category>ストーリー管理</Category>
                  <Button onClick={() => toNovelStudio('write')}>
                    新しいストーリーを書く
                    <Image
                      src={rightArrowIconGray}
                      alt="新しいストーリーを書く"
                    />
                  </Button>
                  <Button onClick={() => toNovelStudio()}>
                    自分のストーリー
                    {tellerUser.searchableStoryCount > 0 ? (
                      <OptionInfo>
                        <Text>{tellerUser.searchableStoryCount}</Text>
                        <Image
                          src={rightArrowIconGray}
                          alt="自分のストーリー"
                        />
                      </OptionInfo>
                    ) : (
                      <Image src={rightArrowIconGray} alt="自分のストーリー" />
                    )}
                  </Button>
                </OptionWrapper>
                <OptionWrapper>
                  <Category>アカウント情報</Category>
                  {SNSProviders.map((p) => (
                    <SNSProvider key={`provider-${p}`}>
                      {p.provider}
                      アカウント
                      <SNSId>{p.id}</SNSId>
                    </SNSProvider>
                  ))}
                  {hasPhoneProvider && (
                    <Option href="/change_phone">
                      電話番号を変更
                      <Image src={rightArrowIconGray} alt="電話番号を変更" />
                    </Option>
                  )}
                  {!hasEmailProvider && (
                    <Option href="/link_email">
                      メールアドレスを連携
                      <Image
                        src={rightArrowIconGray}
                        alt="メールアドレスを連携"
                      />
                    </Option>
                  )}

                  {hasEmailProvider && (
                    <>
                      <Option href="/change_email">
                        メールアドレスを変更
                        <Image
                          src={rightArrowIconGray}
                          alt="メールアドレスを変更"
                        />
                      </Option>
                      <Option href="/change_password">
                        パスワードを変更
                        <Image
                          src={rightArrowIconGray}
                          alt="パスワードを変更"
                        />
                      </Option>
                    </>
                  )}
                  <ExternalOption href="/delete_account">
                    アカウントを削除（退会）
                    <Image
                      src={rightArrowIconGray}
                      alt="アカウントを削除（退会）"
                    />
                  </ExternalOption>
                  {hasPaymentInformation && (
                    <Option href={routes.profile.subscriptionPlan.url()}>
                      {routes.profile.subscriptionPlan.title()}
                      <Image
                        src={rightArrowIconGray}
                        alt="プラン・お支払い情報"
                      />
                    </Option>
                  )}

                  <ExternalOption href="/settings/sensitive">
                    センシティブな内容の設定
                    <Image
                      src={rightArrowIconGray}
                      alt="センシティブな内容の設定"
                    />
                  </ExternalOption>
                </OptionWrapper>
                <OptionWrapper>
                  <Category>ヘルプ</Category>
                  <Option
                    href="https://help.teller.jp/a/teller/?hpn=1&p=web&han=1&l=ja&contact=1"
                    target="_blank"
                  >
                    お問い合わせ
                    <Image src={rightArrowIconGray} alt="お問い合わせ" />
                  </Option>
                  <Option
                    href="https://help.teller.jp/a/teller/?hpn=1&p=web&han=1&l=ja"
                    target="_blank"
                  >
                    よくある質問
                    <Image src={rightArrowIconGray} alt="よくある質問" />
                  </Option>
                </OptionWrapper>
              </VStack>
            </Container>
          </>
        )}
      </MinimalLayout>
      {tellerUser && followListModalType && !isMobile && screenLarge && (
        <FollowListingsModal
          isOpen={isFollowListingsModalOpen}
          onClose={closeFollowListingsModal}
          userId={tellerUser.id}
          type={followListModalType}
          handleTabClick={handleTabClick}
        />
      )}
    </>
  );
};

const Text = styled(CharaText)`
  color: ${Color.ACCENT_700};
  font-size: 1rem;
  word-break: break-all;
  font-family: 'Avenir Next', 'Hiragino Kaku Gothic Pro', 'ヒラギノ角ゴ Pro W3',
    'Hiragino Sans', Meiryo, メイリオ, Osaka, 'MS PGothic', arial, helvetica,
    sans-serif;
  line-height: 1.7;
`;

const Category = styled(Text)`
  margin-top: 40px;
  font-size: ${FontSize.SIZE_12};
  color: ${Color.ACCENT_700};
  margin-bottom: 8px;

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const OptionInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 55px;
`;

const Button = styled.button`
  display: flex;
  cursor: pointer;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  color: ${Color.ACCENT_1000};
  border-top: 1px solid ${Color.SEPARATOR};

  &:hover {
    opacity: 0.7;
  }

  &:last-child {
    border-bottom: 1px solid ${Color.SEPARATOR};
  }

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const OptionWrapper = styled.div`
  width: 100%;
`;

const SNSProvider = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  color: ${Color.ACCENT_1000};
  border-top: 1px solid ${Color.SEPARATOR};

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const SNSId = styled.span`
  color: ${Color.ACCENT_500};
`;
const Option = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  color: ${Color.ACCENT_1000};
  border-top: 1px solid ${Color.SEPARATOR};

  &:last-child {
    border-bottom: 1px solid ${Color.SEPARATOR};
  }

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const ExternalOption = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  color: ${Color.ACCENT_1000};
  border-top: 1px solid ${Color.SEPARATOR};

  &:last-child {
    border-bottom: 1px solid ${Color.SEPARATOR};
  }

  @media ${Media.MEDIUM} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const Animation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PageWrapper = styled(Paper)`
  animation: ${Animation} 200ms ease-in;
  margin-bottom: 16px;
  background-color: ${Color.PRIMARY};
  min-height: 60vh;

  @media ${Media.MEDIUM} {
    .google-auto-placed {
      display: none;
    }
  }
`;
